import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import Navbar from '@/components/Navbar';
import { formatReadingTime } from '@/components/ReadingTime';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Heart, 
  Loader2, 
  Trash2, 
  Clock, 
  Star, 
  Lock,
  X
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Favorites() {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const res = await api.get('/favorites');
      setFavorites(res.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Erro ao carregar favoritos');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (storyId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setRemovingId(storyId);
    try {
      await api.delete(`/favorites/${storyId}`);
      setFavorites(prev => prev.filter(s => s.id !== storyId));
      toast.success('Removido dos favoritos');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Erro ao remover dos favoritos');
    } finally {
      setRemovingId(null);
    }
  };

  const clearAllFavorites = async () => {
    setClearing(true);
    try {
      await api.delete('/favorites');
      setFavorites([]);
      toast.success('Todos os favoritos foram removidos');
      setShowClearDialog(false);
    } catch (error) {
      console.error('Error clearing favorites:', error);
      toast.error('Erro ao limpar favoritos');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-20 sm:pt-24 pb-24 sm:pb-12 px-4 sm:px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-ruby/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-ruby" />
              </div>
              <div>
                <h1 className="font-brand text-2xl font-bold text-foreground">Os Meus Favoritos</h1>
                <p className="text-muted-foreground">{favorites.length} {favorites.length === 1 ? 'conto guardado' : 'contos guardados'}</p>
              </div>
            </div>
            {favorites.length > 0 && (
              <Button
                variant="outline"
                onClick={() => setShowClearDialog(true)}
                data-testid="clear-favorites-btn"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Tudo
              </Button>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-ruby" />
            </div>
          ) : favorites.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {favorites.map(story => (
                <Link 
                  to={`/story/${story.slug || story.id}`} 
                  key={story.id}
                  data-testid={`favorite-card-${story.id}`}
                  className="group bg-surface rounded-xl border border-border overflow-hidden hover:border-ruby/50 transition-all duration-300"
                >
                  <div className="flex gap-4 p-4">
                    {/* Cover */}
                    <div className="relative w-20 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={story.cover_image || 'https://images.unsplash.com/photo-1605547446826-efe6b45ffe4d?w=400'}
                        alt={story.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      {story.is_premium && (
                        <div className="absolute top-1 right-1 w-5 h-5 bg-gold/90 rounded flex items-center justify-center">
                          <Lock className="w-3 h-3 text-foreground" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0 flex flex-col">
                      <h3 className="font-brand text-foreground font-semibold line-clamp-2 group-hover:text-ruby transition-colors">
                        {story.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {story.author_name}
                      </p>
                      
                      <div className="flex items-center gap-3 mt-auto text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatReadingTime(story.reading_time)}
                        </span>
                        {story.rating > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-gold fill-gold" />
                            {story.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={(e) => removeFavorite(story.id, e)}
                      disabled={removingId === story.id}
                      data-testid={`remove-favorite-${story.id}`}
                      className="self-start p-2 rounded-lg text-muted-foreground hover:text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      {removingId === story.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <X className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 rounded-full bg-ruby/10 flex items-center justify-center mx-auto mb-6">
                <Heart className="w-10 h-10 text-ruby/50" />
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-2">
                Nenhum favorito ainda
              </h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Adiciona contos aos favoritos clicando no ícone do coração para aceder rapidamente aos seus preferidos
              </p>
              <Button
                onClick={() => navigate('/browse')}
                data-testid="favorites-browse"
                className="bg-ruby hover:bg-ruby-light rounded-full"
              >
                Explorar Contos
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Clear All Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent className="bg-surface border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Limpar todos os favoritos?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá remover todos os {favorites.length} contos dos seus favoritos. Esta ação não pode ser revertida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={clearAllFavorites}
              disabled={clearing}
              data-testid="confirm-clear-favorites"
              className="bg-red-600 hover:bg-red-700"
            >
              {clearing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Limpar Tudo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
